import React, { useEffect, useState } from "react";
import { Loader2, Eye, EyeOff, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

export const fmtFecha = (d) => {
  if (!d) return "";
  const s = typeof d === "string" && !/[zZ]|[+-]\d\d:\d\d$/.test(d) ? `${d}Z` : d;
  const dt = new Date(s);
  return isNaN(dt) ? "" : dt.toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const PAGO_BADGE = {
  al_dia: ["Al día", "#22A860"], deudor: ["Con adeudo", "#E67E22"], suspendido: ["Suspendido", "#C0392B"],
  sin_config: ["Sin plan de pagos", "#6B7688"], no_aplica: ["No aplica", "#9AA3B2"],
};

export const PagoBadge = ({ estado, testid }) => {
  const [label, color] = PAGO_BADGE[estado] || PAGO_BADGE.no_aplica;
  return <span data-testid={testid} className="inline-block px-2.5 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap" style={{ background: color }}>{label}</span>;
};

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Super Admin", manager: "Manager", staff: "Staff", instructor: "Instructor", invitado: "Invitado", user: "Usuario" };

const Row = ({ label, children, testid }) => (
  <div className="rounded-xl bg-[#F7F8FA] px-3.5 py-2.5">
    <p className="text-[11px] font-extrabold uppercase tracking-wide text-[#9AA3B2]">{label}</p>
    <div data-testid={testid} className="text-sm font-semibold text-[#0A1C30] break-words mt-0.5">{children || <span className="text-[#B7BFCC]">Sin dato</span>}</div>
  </div>
);

const PasswordRow = ({ d }) => {
  const [show, setShow] = useState(false);
  if (!d.puede_ver_password) return <Row label="Contraseña" testid="ficha-password">Solo visible para el Super Admin</Row>;
  if (!d.password_visible) return <Row label="Contraseña" testid="ficha-password"><span className="text-[#9AA3B2] font-normal">Aún no disponible: aparecerá en cuanto el usuario vuelva a iniciar sesión</span></Row>;
  return (
    <Row label="Contraseña" testid="ficha-password">
      <span className="inline-flex items-center gap-2">
        <span className="font-mono">{show ? d.password_visible : "••••••••"}</span>
        <button data-testid="ficha-password-toggle" onClick={() => setShow((s) => !s)} className="text-[#3B7DF6]" title={show ? "Ocultar" : "Mostrar"}>{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
        <button data-testid="ficha-password-copy" onClick={() => { navigator.clipboard?.writeText(d.password_visible); toast.success("Contraseña copiada."); }} className="text-[#6B7688]" title="Copiar"><Copy className="w-4 h-4" /></button>
      </span>
    </Row>
  );
};

const estadoCuenta = (d) => d.suspendido_manual ? "Suspensión manual" : d.suspendido_pago ? "Suspendido por pago" : d.rol_supera === "Baja" ? "Baja" : "Activo";

export const UserFichaDialog = ({ userId, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (userId) api.get(`/admin/users/${userId}/ficha`).then((r) => setD(r.data)).catch((e) => { toast.error(apiError(e)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  const esPadre = d?.programa === "Padres y Madres";
  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-ficha-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold text-[#0A1C30]">{d ? `${d.nombre || ""} ${d.apellidos || ""}` : "Ficha del usuario"}</DialogTitle>
          <DialogDescription>Toda la información que registró el usuario y su estado en la app.</DialogDescription>
        </DialogHeader>
        {!d ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#9AA3B2]" /></div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <Row label="Nombre(s)" testid="ficha-nombre">{d.nombre}</Row>
            <Row label="Apellidos" testid="ficha-apellidos">{d.apellidos}</Row>
            <Row label="Correo electrónico" testid="ficha-email">{d.email}</Row>
            <PasswordRow d={d} />
            <Row label="WhatsApp" testid="ficha-whatsapp">{d.whatsapp}</Row>
            <Row label="Edad" testid="ficha-edad">{d.edad}</Row>
            <Row label="País" testid="ficha-pais">{d.pais}</Row>
            <Row label="Ciudad" testid="ficha-ciudad">{d.ciudad}</Row>
            <Row label="Programa" testid="ficha-programa">{d.programa}</Row>
            <Row label="Grupo" testid="ficha-grupo">{d.grupo ? `Grupo ${d.grupo}` : ""}</Row>
            <Row label="Colegio" testid="ficha-colegio">{d.colegio}</Row>
            <Row label="Grado académico" testid="ficha-grado">{esPadre ? "No aplica (padre/madre)" : d.grado}</Row>
            <Row label="Sexo" testid="ficha-sexo">{d.sexo === "hombre" ? "Hombre" : d.sexo === "mujer" ? "Mujer" : ""}</Row>
            <Row label="Referencia Supera" testid="ficha-referencia">{d.referencia}</Row>
            <Row label="Rol en la app" testid="ficha-rol">{`${ROLE_LABEL[d.role] || d.role} · ${d.rol_supera}`}</Row>
            <Row label="Estado de la cuenta" testid="ficha-estado">{estadoCuenta(d)}</Row>
            <Row label="Pagos" testid="ficha-pago"><PagoBadge estado={d.estado_pago} /></Row>
            <Row label="Puntos" testid="ficha-puntos">{String(d.puntos ?? 0)}</Row>
            <Row label="Fecha y hora de registro" testid="ficha-registro">{fmtFecha(d.created_at)}</Row>
            <Row label="Último acceso a la app" testid="ficha-ultimo-acceso">{fmtFecha(d.last_access || d.last_login) || <span className="text-[#9AA3B2] font-normal">Sin registro</span>}</Row>
            {esPadre && <Row label="Hijos/as vinculados" testid="ficha-hijos">{(d.hijos || []).map((h) => `${h.nombre} (${h.programa})`).join(", ")}</Row>}
            {!esPadre && (d.candidato_nombre || d.candidato_programa) && <Row label="Candidato/a indicado" testid="ficha-candidato">{`${d.candidato_nombre} ${d.candidato_programa ? `· ${d.candidato_programa}` : ""}`}</Row>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
